import React from 'react';
import { Box, Skeleton } from '@mui/material';
import Divider from '@mui/material/Divider';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import IcBadge from '../../Icons/ic_leak_badge.svg';
import './LeakInspectionsDashboard.css';

const getCardVariant = (rate) => {
    if (rate === null || rate === undefined) return 'gray';
    if (rate >= 100) return 'gold';
    if (rate >= 90)  return 'teal-dark'; // 90-99%: Nearing Full Compliance
    if (rate >= 70)  return 'teal';      // 70-89%: Progressing Towards Compliance
    if (rate >= 50)  return 'salmon';    // 50-69%: Needs Attention
    return 'red';                         // <50%:   Non-Compliant
};

const getStatusLabel = (rate) => {
    if (rate === null || rate === undefined) return 'No Data';
    if (rate >= 100) return 'Compliant';
    if (rate >= 90)  return 'Nearing Full Compliance';
    if (rate >= 70)  return 'Progressing Towards Compliance';
    if (rate >= 50)  return 'Needs Attention';
    return 'Non-Compliant';
};

const StatBlock = ({ label, value }) => (
    <div className="lid-card__stat">
        <span className="lid-card__stat-label">{label}</span>
        <span className="lid-card__stat-value">{value ?? '-'}</span>
    </div>
);

const LeakInspectionComplianceCard = ({ summary, loading }) => {
    if (loading) {
        return (
            <Box sx={{ width: '100%', height: 272, borderRadius: '10px', p: 3, bgcolor: 'grey.200', display: 'flex', flexDirection: 'column', gap: 3, boxSizing: 'border-box' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <Skeleton variant="text" width={80} height={44} />
                        <Skeleton variant="text" width={160} height={24} />
                    </Box>
                    <Skeleton variant="circular" width={66} height={66} />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <Skeleton variant="text" width={120} height={18} sx={{ alignSelf: 'flex-end' }} />
                    <Skeleton variant="rectangular" height={30} sx={{ borderRadius: '8px' }} />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Skeleton variant="text" width="30%" height={50} />
                    <Skeleton variant="text" width="30%" height={50} />
                    <Skeleton variant="text" width="30%" height={50} />
                </Box>
            </Box>
        );
    }

    const rate = summary?.overallComplianceRate;
    const variant = getCardVariant(rate);
    const fillPct = Math.min(Math.max(rate ?? 0, 0), 100);
    const isGold   = variant === 'gold';
    const isTeal   = variant === 'teal-dark' || variant === 'teal';
    const isRed    = variant === 'salmon' || variant === 'red';

    const progressFillClass  = isGold ? 'gold' : isTeal ? 'teal' : 'red';
    const progressEmptyClass = isTeal ? 'teal' : 'red';

    const iconColor   = isRed ? '#D32F2F' : '#fff';
    const iconBorder  = isRed ? 'rgba(211,47,47,0.4)' : 'rgba(255,255,255,0.6)';
    const iconBg      = isRed ? 'rgba(211,47,47,0.08)' : 'rgba(255,255,255,0.15)';
    const dividerColor = isRed ? 'rgba(211,47,47,0.25)' : 'rgba(255,255,255,0.3)';

    return (
        <div className={`lid-card lid-card--${variant}`}>

            {/* ── Row 1: rate + label (+ badge for gold only) ── */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div className="lid-card__rate">
                        {rate != null ? `${rate}%` : '-'}
                    </div>
                    <div className="lid-card__label">Overall Compliance Rate</div>
                </Box>

                {isGold && (
                    <div style={{ position: 'relative', width: 66, height: 66, flexShrink: 0 }}>
                        <svg style={{ position: 'absolute', inset: 0 }} width="66" height="66">
                            <defs>
                                <linearGradient id="badge-ring-grad" x1="0" y1="0" x2="1" y2="1">
                                    <stop offset="0%"   stopColor="#F6E190" />
                                    <stop offset="25%"  stopColor="#C2A04A" />
                                    <stop offset="50%"  stopColor="#AF8930" />
                                    <stop offset="75%"  stopColor="#9D7318" />
                                    <stop offset="100%" stopColor="#896414" />
                                </linearGradient>
                            </defs>
                            <circle cx="33" cy="33" r="30" fill="none" stroke="url(#badge-ring-grad)" strokeWidth="3" />
                        </svg>
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img src={IcBadge} alt="compliance badge" width={32} height={42} />
                        </div>
                    </div>
                )}
            </Box>

            {/* ── Row 2: progress bar section ── */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>

                {/* Gold: status ABOVE bar */}
                {isGold && (
                    <div className="lid-card__status lid-card__status--above">
                        {getStatusLabel(rate)}
                    </div>
                )}

                {/* Non-gold: running icon above bar at fill% position */}
                {!isGold && (
                    <Box sx={{ position: 'relative', height: 26, mb: '4px' }}>
                        <Box sx={{
                            position: 'absolute',
                            left: `${fillPct}%`,
                            transform: 'translateX(-50%)',
                            width: 26,
                            height: 26,
                            borderRadius: '50%',
                            border: `1.5px solid ${iconBorder}`,
                            backgroundColor: iconBg,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: iconColor,
                        }}>
                            <DirectionsRunIcon sx={{ fontSize: '1rem' }} />
                        </Box>
                    </Box>
                )}

                {/* Progress bar */}
                <div className={`lid-card__progress-bar${!isGold ? ` lid-card__progress-bar--${progressEmptyClass}` : ''}`}>
                    <div
                        className={`lid-card__progress-fill lid-card__progress-fill--${progressFillClass}`}
                        style={{ width: `${fillPct}%` }}
                    />
                    {!isGold && fillPct < 100 && (
                        <div className={`lid-card__progress-empty--${progressEmptyClass}`} />
                    )}
                </div>

                {/* Non-gold: status BELOW bar, right-aligned */}
                {!isGold && (
                    <div className="lid-card__status lid-card__status--below">
                        {getStatusLabel(rate)}
                    </div>
                )}
            </Box>

            {/* ── Row 3: stats with MUI vertical dividers ── */}
            <div className="lid-card__stats">
                <StatBlock label="Required"  value={summary?.overallRequired} />
                <Divider orientation="vertical" flexItem sx={{ borderColor: dividerColor, height: 50, alignSelf: 'center' }} />
                <StatBlock label="Completed" value={summary?.overallCompleted} />
                <Divider orientation="vertical" flexItem sx={{ borderColor: dividerColor, height: 50, alignSelf: 'center' }} />
                <StatBlock label="Missed"    value={summary?.overallMissed} />
            </div>
        </div>
    );
};

export default LeakInspectionComplianceCard;
